import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import { CountResponseSchema } from "../ea.schema.endpoints";
import { UserSchemas, RoleSchemas, UserGroupSchemas } from "../ea.schema";
import {
  GetRequestBaseResponseSchema,
  UserFilterSchema,
  UserGroupsFilterSchema,
} from "../ea.schema.query";

const usersBase = "users";
const users = createEndpointBase(usersBase);
const usersGroups = createEndpointBase(usersBase, "groups");
const usersRoles = createEndpointBase(usersBase, "roles");

export const usersEndpoints = {
  usersGet: users({
    method: "GET",
    path: "",
    request: z.object({ filter: UserFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(UserSchemas["general"]),
    description: "Retrieves a list of user accounts with filtering support.",
  }),
  usersCount: users({
    method: "GET",
    path: "count",
    request: z.object({ filter: UserFilterSchema.pick({ where: true }).optional() }).optional(),
    response: CountResponseSchema,
    description: "Returns the total number of user accounts matching the filters.",
  }),
  usersCreate: users({
    method: "POST",
    path: "",
    request: UserSchemas["createData"],
    response: UserSchemas["general"],
    description: "Creates a new user account.",
  }),
  usersUpdate: users({
    method: "PATCH",
    path: ":id",
    request: UserSchemas["update"],
    response: UserSchemas["general"],
    // Note: Removed explicit 'requestType: {} as any' to allow proper type inference
    // Now gets: UserSchemas["update"] + { id: string | number } (from path)
    description: "Updates an existing user account.",
  }),
  usersDelete: users({
    method: "DELETE",
    path: ":id",
    request: z.any(),
    response: z.any(),
    description: "Permanently deletes a user account.",
  }),
  usersAuth: users({
    method: "GET",
    path: "auth",
    request: z.any(),
    response: UserSchemas["general"],
    description: "Retrieves information about the currently authenticated user.",
  }),
  usersAuthorize: users({
    method: "POST",
    path: "authorize",
    request: UserSchemas["login"].pick({ username: true, password: true }),
    response: UserSchemas["loginResponse"].pick({ id: true }),
    description: "Verifies user credentials without logging in.",
  }),
  usersLogin: users({
    method: "POST",
    path: "login",
    request: UserSchemas["login"],
    response: UserSchemas["loginResponse"],
    description: "Logs in a user and returns an authentication token.",
  }),
  usersLogout: users({
    method: "POST",
    path: "logout",
    request: z.any(),
    response: z.any(),
    description: "Logs out the current user by invalidating the token.",
  }),
  usersChangePassword: users({
    method: "POST",
    path: "change-password",
    request: UserSchemas["changePassword"],
    response: z.any(),
    description: "Changes the password for a user account.",
  }),

  // groups
  usersGroupsGet: usersGroups({
    method: "GET",
    path: "",
    request: z.object({ filter: UserGroupsFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(UserGroupSchemas["general"]),
    description: "Retrieves a list of user groups with filtering support.",
  }),
  usersGroupsCount: usersGroups({
    method: "GET",
    path: "count",
    request: z
      .object({ filter: UserGroupsFilterSchema.pick({ where: true }).optional() })
      .optional(),
    response: CountResponseSchema,
    description: "Returns the total number of user groups matching the filters.",
  }),
  usersGroupsCreate: usersGroups({
    method: "POST",
    path: "",
    request: UserGroupSchemas["createData"],
    response: UserGroupSchemas["general"],
    description: "Creates a new user group.",
  }),
  usersGroupsUpdate: usersGroups({
    method: "PATCH",
    path: ":id",
    request: UserGroupSchemas["update"],
    response: UserGroupSchemas["general"],
    description: "Updates an existing user group.",
  }),
  usersGroupsDelete: usersGroups({
    method: "DELETE",
    path: ":id",
    request: z.any(),
    response: z.any(),
    description: "Permanently deletes a user group.",
  }),

  // roles
  usersRolesGet: usersRoles({
    method: "GET",
    path: "",
    request: z.any(),
    response: z.array(RoleSchemas["general"]),
    description: "Retrieves all user roles.",
  }),
  usersRolesCount: usersRoles({
    method: "GET",
    path: "count",
    request: z.any(),
    response: CountResponseSchema,
    description: "Returns the total number of user roles.",
  }),
  usersRolesCreate: usersRoles({
    method: "POST",
    path: "",
    request: RoleSchemas["createData"],
    response: RoleSchemas["general"],
  }),
  usersRolesUpdate: usersRoles({
    method: "PATCH",
    path: ":id",
    request: RoleSchemas["update"],
    response: RoleSchemas["general"],
  }),
  usersRolesDelete: usersRoles({
    method: "DELETE",
    path: ":id",
    request: z.any(),
    response: z.any(),
  }),
} as const;
